import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { VaultDoor, type VaultDoorState } from '@/components/illustrations/VaultDoor';
import { Button } from '@/components/ui/Button';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { useAuth } from '@/lib/supabase/AuthContext';
import { vaultConfig } from '@/lib/supabase/client';
import { spring } from '@/lib/motion/presets';

/* ============================================================
   WelcomeScreen — the lock screen / hero of the app.

   Three-act state machine:
     idle → authenticating → unlocked → (router pushes to /vault)

   Side states layered on top:
     - wrong-password shake (transient, returns to idle)
     - rate-limit countdown (3+ failed attempts)

   Visual concept: dark canvas with the animated VaultDoor centered,
   "Welcome back, [name]" in Instrument Serif below, monospace password
   input with reveal toggle, footer showing the domain as anti-phishing anchor.
   ============================================================ */

type Phase = 'idle' | 'authenticating' | 'unlocked';

const MAX_ATTEMPTS_BEFORE_COOLDOWN = 3;
const COOLDOWN_SECONDS = 30;

export function WelcomeScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, session, loading } = useAuth();

  const [phase, setPhase] = useState<Phase>('idle');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [shake, setShake] = useState(0);
  const [inputFocused, setInputFocused] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);
  const redirectedRef = useRef(false);

  // If already authenticated, push to vault.
  useEffect(() => {
    if (loading) return;
    if (session && !redirectedRef.current) {
      redirectedRef.current = true;
      const from = (location.state as { from?: string } | null)?.from ?? '/vault';
      navigate(from, { replace: true });
    }
  }, [session, loading, navigate, location.state]);

  // Cooldown ticker
  useEffect(() => {
    if (!cooldownUntil) {
      setCooldownRemaining(0);
      return;
    }
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((cooldownUntil - Date.now()) / 1000));
      setCooldownRemaining(remaining);
      if (remaining === 0) setCooldownUntil(null);
    };
    tick();
    const id = window.setInterval(tick, 250);
    return () => window.clearInterval(id);
  }, [cooldownUntil]);

  // Auto-focus input on mount
  useEffect(() => {
    const id = window.setTimeout(() => inputRef.current?.focus(), 600);
    return () => window.clearTimeout(id);
  }, []);

  const inCooldown = cooldownUntil !== null && cooldownRemaining > 0;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (phase !== 'idle' || inCooldown) return;
    if (!password) return;

    setError(null);
    setPhase('authenticating');

    const { error: err } = await signIn(vaultConfig.email, password);

    if (err) {
      const nextAttempts = attempts + 1;
      setAttempts(nextAttempts);

      // Wrong-password shake — bump a counter so AnimatePresence re-keys
      setShake((s) => s + 1);

      if (nextAttempts >= MAX_ATTEMPTS_BEFORE_COOLDOWN) {
        setCooldownUntil(Date.now() + COOLDOWN_SECONDS * 1000);
      }

      setError(err);
      setPhase('idle');
      setPassword('');
      // refocus after the shake
      window.setTimeout(() => inputRef.current?.focus(), 320);
      return;
    }

    // Success — open the vault, then navigate
    setPhase('unlocked');
    window.setTimeout(() => {
      const from = (location.state as { from?: string } | null)?.from ?? '/vault';
      navigate(from, { replace: true });
    }, 750);
  }

  const vaultState: VaultDoorState =
    phase === 'authenticating' ? 'authenticating' : phase === 'unlocked' ? 'unlocked' : 'idle';

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-bg-deep text-ink">
      {/* Layered ambient backdrop */}
      <BackdropAura />

      {/* Top bar — domain confirmation (anti-phishing anchor) */}
      <header className="relative z-10 flex items-center justify-between px-6 py-5 sm:px-10">
        <div className="flex items-center gap-2 text-ink-faint">
          <span className="inline-block size-1.5 rounded-full bg-success shadow-[0_0_10px_rgb(var(--c-success))]" />
          <span className="text-mono text-2xs uppercase tracking-[0.18em]">
            Secure connection
          </span>
        </div>
        <div className="text-mono text-2xs text-ink-faint">
          <span className="hidden sm:inline">You are on · </span>
          <span className="text-ink-muted">{vaultConfig.domain}</span>
        </div>
      </header>

      {/* Main stage */}
      <main className="relative z-10 flex min-h-[calc(100vh-72px)] flex-col items-center justify-center px-6 pb-16">
        {/* Vault door — fixed visual weight, animated by phase */}
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ ...spring, delay: 0.1, duration: 0.8 }}
          className="relative"
        >
          <AnimatePresence mode="popLayout">
            <motion.div
              key={shake}
              animate={
                shake > 0
                  ? { x: [0, -8, 8, -6, 6, -3, 3, 0] }
                  : { x: 0 }
              }
              transition={{ duration: 0.45, ease: [0.36, 0.07, 0.19, 0.97] }}
              style={{ transformOrigin: 'center' }}
            >
              <VaultDoor state={vaultState} size={360} className="drop-shadow-[0_24px_48px_rgba(0,0,0,0.6)]" />
            </motion.div>
          </AnimatePresence>
        </motion.div>

        {/* Greeting */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...spring, delay: 0.35 }}
          className="mt-10 text-center"
        >
          <h1 className="text-serif text-5xl italic leading-none text-ink sm:text-6xl">
            Welcome back, <span className="not-italic font-sans font-medium tracking-tight text-ink">{vaultConfig.displayName}</span>
          </h1>
          <p className="mt-3 text-sm text-ink-muted">
            {phase === 'unlocked'
              ? 'Opening your vault…'
              : phase === 'authenticating'
                ? 'Verifying your password…'
                : 'Enter your password to continue'}
          </p>
        </motion.div>

        {/* Form */}
        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...spring, delay: 0.5 }}
          className="mt-8 w-full max-w-sm"
          noValidate
        >
          <PasswordInput
            ref={inputRef}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (error) setError(null);
            }}
            onFocus={() => setInputFocused(true)}
            onBlur={() => setInputFocused(false)}
            focused={inputFocused}
            invalid={!!error}
            disabled={phase !== 'idle' || inCooldown}
            placeholder="••••••••••••"
            aria-label="Master password"
            autoFocus
          />

          {/* Error / cooldown message */}
          <div className="mt-3 min-h-[20px] text-xs">
            <AnimatePresence mode="wait">
              {error && phase === 'idle' && (
                <motion.p
                  key="error"
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.18 }}
                  className="text-danger"
                  role="alert"
                >
                  {error}
                  {inCooldown && (
                    <span className="text-ink-muted">
                      {' '}Try again in {cooldownRemaining}s.
                    </span>
                  )}
                </motion.p>
              )}
              {phase === 'authenticating' && (
                <motion.p
                  key="auth"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="text-ink-muted"
                >
                  Unlocking…
                </motion.p>
              )}
            </AnimatePresence>
          </div>

          {/* Submit */}
          <div className="mt-4 flex items-center justify-between gap-3">
            <span className="text-2xs uppercase tracking-[0.18em] text-ink-faint">
              Press Enter
            </span>
            <Button
              type="submit"
              variant="primary"
              size="md"
              disabled={!password || phase !== 'idle' || inCooldown}
              loading={phase === 'authenticating'}
            >
              {phase === 'unlocked' ? 'Opening' : 'Unlock'}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14" />
                <path d="m12 5 7 7-7 7" />
              </svg>
            </Button>
          </div>
        </motion.form>

        {/* Footer hint */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.6 }}
          className="mt-16 text-2xs uppercase tracking-[0.22em] text-ink-faint"
        >
          Your vault · your data · your subdomain
        </motion.p>
      </main>
    </div>
  );
}

/* -- Ambient backdrop ---------------------------------------------- */

function BackdropAura() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0">
      {/* Soft accent glow centered behind the door */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.4 }}
        className="absolute left-1/2 top-[42%] -translate-x-1/2 -translate-y-1/2"
      >
        <div className="size-[680px] rounded-full bg-[radial-gradient(circle_at_center,rgba(255,106,61,0.10)_0%,rgba(255,106,61,0)_60%)] blur-3xl" />
      </motion.div>
      {/* Subtle grid lines (very faint) */}
      <svg className="absolute inset-0 h-full w-full opacity-[0.025]" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="grid" width="56" height="56" patternUnits="userSpaceOnUse">
            <path d="M 56 0 L 0 0 0 56" fill="none" stroke="white" strokeWidth="1" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)" />
      </svg>
      {/* Top vignette */}
      <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-bg-deep to-transparent" />
      {/* Bottom vignette */}
      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-bg-deep to-transparent" />
    </div>
  );
}